import { 
    Box, 
    Typography, 
    Button, 
    Menu, 
    MenuItem, 
    IconButton, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    TextField, 
    DialogActions, 
    FormControl, 
    InputLabel, 
    Select, 
    Alert, 
    Chip,
    Grid,
    Card,
    CardContent,
    CardActions,
    Switch,
    FormControlLabel,
    Divider
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useState, useMemo } from "react";
import { useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import moment from "moment";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { 
    useGetEmployeesQuery,
    useGetAllAvailabilitiesQuery,
    useCreateAvailabilityMutation,
    useUpdateAvailabilityMutation,
    useDeleteAvailabilityMutation,
    useProcessAvailabilityMutation
} from "../../state/api";

const Availability = () => {
    const theme = useTheme();
    const isMobileScreen = useMediaQuery('(max-width:768px)');
    
    // Get current user ID from Redux state
    const currentUserId = useSelector((state) => state.global.userId);
    
    const { data: employeesResponse, isLoading: isLoadingEmployees, isError: isErrorEmployees } = useGetEmployeesQuery();
    const { data: availabilitiesResponse, isLoading: isLoadingAvailabilities, isError: isErrorAvailabilities } = useGetAllAvailabilitiesQuery();
    const [createAvailability] = useCreateAvailabilityMutation();
    const [updateAvailability] = useUpdateAvailabilityMutation();
    const [deleteAvailability] = useDeleteAvailabilityMutation();
    const [processAvailability] = useProcessAvailabilityMutation();
    
    const employeesData = employeesResponse?.data;
    const allAvailabilities = availabilitiesResponse || [];
    
    const [selectedFilter, setSelectedFilter] = useState("Pending");
    const filterOptions = ["Pending", "Approved", "Rejected", "All"];
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [openProcessDialog, setOpenProcessDialog] = useState(false);
    const [processAction, setProcessAction] = useState('');
    const [rejectionReason, setRejectionReason] = useState('');
    const [editableAvailabilityData, setEditableAvailabilityData] = useState(null);
    const [validationAlert, setValidationAlert] = useState({ show: false, message: '', severity: 'warning' });
    
    const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    
    const [newAvailabilityData, setNewAvailabilityData] = useState({
        employeeId: "",
        typeOfAvailability: "Permanent",
        notes: "",
        startDate: moment().format("YYYY-MM-DD"),
        endDate: moment().add(30, 'days').format("YYYY-MM-DD"),
        weeklyPattern: daysOfWeek.map(day => ({
            dayOfWeek: day,
            isAvailable: false,
            startTime: "09:00",
            endTime: "17:00"
        }))
    });

    const handleMenuClick = (event, params) => {
        setAnchorEl(event.currentTarget);
        setSelectedRow(params.row);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleViewOpen = () => {
        setOpenViewDialog(true);
        handleMenuClose();
    };

    const handleEditOpen = () => {
        // Ensure weekly pattern is properly formatted for editing
        let weeklyPattern = selectedRow.weeklyPattern;
        if (!weeklyPattern || !Array.isArray(weeklyPattern)) {
            // Initialize with default pattern if missing
            weeklyPattern = daysOfWeek.map(day => ({
                dayOfWeek: day,
                isAvailable: false,
                startTime: "09:00",
                endTime: "17:00"
            }));
        } else {
            // Ensure all days have proper time format
            weeklyPattern = weeklyPattern.map(day => ({
                ...day,
                startTime: day.startTime || "09:00",
                endTime: day.endTime || "17:00",
                isAvailable: Boolean(day.isAvailable)
            }));
        }

        setEditableAvailabilityData({
            ...selectedRow,
            // Ensure employeeId is always a string, not an object
            employeeId: typeof selectedRow.employeeId === 'object' ? selectedRow.employeeId._id : selectedRow.employeeId,
            startDate: selectedRow.startDate ? moment(selectedRow.startDate).format("YYYY-MM-DD") : "",
            endDate: selectedRow.endDate ? moment(selectedRow.endDate).format("YYYY-MM-DD") : "",
            weeklyPattern: weeklyPattern
        });
        setOpenEditDialog(true);
        handleMenuClose();
    };

    const handleEditSubmit = async () => {
        if (editableAvailabilityData) {
            try {
                const updateData = {
                    ...editableAvailabilityData,
                    startDate: editableAvailabilityData.startDate ? new Date(editableAvailabilityData.startDate).toISOString() : null,
                    endDate: editableAvailabilityData.endDate ? new Date(editableAvailabilityData.endDate).toISOString() : null,
                };

                await updateAvailability({ id: editableAvailabilityData._id, ...updateData });
                setOpenEditDialog(false);
                setValidationAlert({ show: false, message: '', severity: 'warning' });
            } catch (error) {
        
                setValidationAlert({ show: true, message: `Failed to update availability: ${error.data?.message || error.message}`, severity: 'error' });
            }
        }
    };

    const handleAddDialogOpen = () => {
        setOpenAddDialog(true);
        setValidationAlert({ show: false, message: '', severity: 'warning' });
    };
    
    const handleAddDialogClose = () => {
        setOpenAddDialog(false);
        setValidationAlert({ show: false, message: '', severity: 'warning' });
        setNewAvailabilityData({
            employeeId: "",
            typeOfAvailability: "Permanent",
            notes: "",
            startDate: moment().format("YYYY-MM-DD"),
            endDate: moment().add(30, 'days').format("YYYY-MM-DD"),
            weeklyPattern: daysOfWeek.map(day => ({
                dayOfWeek: day,
                isAvailable: false,
                startTime: "09:00",
                endTime: "17:00"
            }))
        });
    };

    const handleNewAvailabilityInputChange = (e) => {
        const { name, value, type, checked } = e.target;
    
        setNewAvailabilityData(prev => ({ 
            ...prev, 
            [name]: type === 'checkbox' ? checked : value 
        }));
    };

    const handleWeeklyPatternChange = (dayIndex, field, value) => {
        setNewAvailabilityData(prev => ({
            ...prev,
            weeklyPattern: prev.weeklyPattern.map((day, index) => {
                if (index === dayIndex) {
                    const updatedDay = { ...day, [field]: value };
                    
                    // Validate time ranges when time fields are changed
                    if (field === 'startTime' && updatedDay.endTime && value >= updatedDay.endTime) {
                        // If start time is >= end time, adjust end time
                        const startMoment = moment(value, 'HH:mm');
                        const newEndTime = startMoment.add(1, 'hour').format('HH:mm');
                        updatedDay.endTime = newEndTime;
                    } else if (field === 'endTime' && updatedDay.startTime && value <= updatedDay.startTime) {
                        // If end time is <= start time, adjust start time
                        const endMoment = moment(value, 'HH:mm');
                        const newStartTime = endMoment.subtract(1, 'hour').format('HH:mm');
                        updatedDay.startTime = newStartTime;
                    }
                    
                    return updatedDay;
                }
                return day;
            })
        }));
    };

    const handleEditWeeklyPatternChange = (dayIndex, field, value) => {
        setEditableAvailabilityData(prev => ({
            ...prev,
            weeklyPattern: prev.weeklyPattern.map((day, index) => {
                if (index === dayIndex) {
                    const updatedDay = { ...day, [field]: value };
                    
                    // Validate time ranges when time fields are changed
                    if (field === 'startTime' && updatedDay.endTime && value >= updatedDay.endTime) {
                        // If start time is >= end time, adjust end time
                        const startMoment = moment(value, 'HH:mm');
                        const newEndTime = startMoment.add(1, 'hour').format('HH:mm');
                        updatedDay.endTime = newEndTime;
                    } else if (field === 'endTime' && updatedDay.startTime && value <= updatedDay.startTime) {
                        // If end time is <= start time, adjust start time
                        const endMoment = moment(value, 'HH:mm');
                        const newStartTime = endMoment.subtract(1, 'hour').format('HH:mm');
                        updatedDay.startTime = newStartTime;
                    }
                    
                    return updatedDay;
                }
                return day;
            })
        }));
    };
    
    const handleAddSubmit = async () => {
        if (!newAvailabilityData.employeeId) {
            setValidationAlert({ show: true, message: "Please select an employee.", severity: 'error' });
            return;
        }

        // Validation for temporary availability
        if (newAvailabilityData.typeOfAvailability === 'Temporary') {
            if (!newAvailabilityData.startDate || !newAvailabilityData.endDate) {
                setValidationAlert({ show: true, message: "Start date and end date are required for temporary availability.", severity: 'error' });
                return;
            }
        }

        try {
            const availabilityToCreate = {
                ...newAvailabilityData,
                startDate: newAvailabilityData.typeOfAvailability === 'Temporary' ? new Date(newAvailabilityData.startDate).toISOString() : null,
                endDate: newAvailabilityData.typeOfAvailability === 'Temporary' ? new Date(newAvailabilityData.endDate).toISOString() : null,
                userId: currentUserId,
            };

            await createAvailability(availabilityToCreate).unwrap();
            handleAddDialogClose();
        } catch (error) {
    
            setValidationAlert({ show: true, message: `Failed to create availability: ${error.data?.message || error.message}`, severity: 'error' });
        }
    };
    
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditableAvailabilityData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleDeleteOpen = () => {
        setOpenDeleteDialog(true);
        handleMenuClose();
    };

    const handleDelete = async () => {
        if (selectedRow) {
            try {
                await deleteAvailability(selectedRow._id);
                setOpenDeleteDialog(false);
            } catch (error) {
        
            }
        }
    };

    const processedData = useMemo(() => {
        if (!allAvailabilities || !employeesData || isLoadingAvailabilities || isLoadingEmployees) {
            return [];
        }

        let dataToProcess = Array.isArray(allAvailabilities) ? allAvailabilities : [];

        if (dataToProcess.length === 0) return [];

        let filteredData = dataToProcess;
        
        // Apply status filter
        if (selectedFilter !== "All") {
            filteredData = dataToProcess.filter(availability => availability.status === selectedFilter);
        }

        return filteredData.map(availability => {
            let employeeName = 'Unknown Employee';
            let approvedByName = '';
            
            // Check if employeeId is populated (is an object) or just an ID string
            if (availability.employeeId && typeof availability.employeeId === 'object') {
                const { firstName, lastName } = availability.employeeId.names;
                employeeName = `${firstName} ${lastName}`;
            } else {
                // Fallback for non-populated ID
                const employee = employeesData.find(emp => emp._id === availability.employeeId);
                if (employee) {
                    employeeName = `${employee.names.firstName} ${employee.names.lastName}`;
                }
            }
            
            // Handle approvedBy field - check if it's populated
            if (availability.approvedBy && typeof availability.approvedBy === 'object') {
                approvedByName = `${availability.approvedBy.firstName} ${availability.approvedBy.lastName}`;
            }
            
            // Get available days summary
            const availableDays = availability.weeklyPattern
                .filter(day => day.isAvailable)
                .map(day => day.dayOfWeek.substring(0, 3))
                .join(', ');
            
            return {
                ...availability,
                id: availability._id,
                employeeName,
                approvedByName,
                availableDays: availableDays || 'No days available',
                displayStartDate: availability.startDate ? moment(availability.startDate).format("DD/MM/YYYY") : 'N/A',
                displayEndDate: availability.endDate ? moment(availability.endDate).format("DD/MM/YYYY") : 'N/A',
                displayLastUpdated: moment(availability.lastUpdated).format("DD/MM/YYYY"),
                displayProcessedAt: availability.processedAt ? moment(availability.processedAt).format("DD/MM/YYYY") : 'N/A',
            };
        });
    }, [allAvailabilities, employeesData, isLoadingAvailabilities, isLoadingEmployees, selectedFilter]);

    const columns = [
        {
            field: "employeeName",
            headerName: "Employee",
            flex: 1,
            minWidth: 150,
        },
        {
            field: "status",
            headerName: "Status",
            flex: 0.8,
            minWidth: 100,
            renderCell: (params) => {
                const getStatusColor = (status) => {
                    switch (status) {
                        case 'Approved': return 'success';
                        case 'Rejected': return 'error';
                        case 'Pending': return 'warning';
                        default: return 'default';
                    }
                };
                return (
                    <Chip 
                        label={params.value} 
                        color={getStatusColor(params.value)}
                        size="small"
                    />
                );
            },
        },
        {
            field: "typeOfAvailability",
            headerName: "Type",
            flex: 0.8,
            minWidth: 100,
            renderCell: (params) => (
                <Chip 
                    label={params.value} 
                    color={params.value === 'Permanent' ? 'primary' : 'secondary'}
                    size="small"
                />
            ),
        },
        {
            field: "availableDays",
            headerName: "Available Days",
            flex: 1.5,
            minWidth: 200,
        },
        {
            field: "displayStartDate",
            headerName: "Start Date",
            flex: 1,
            minWidth: 120,
        },
        {
            field: "displayEndDate",
            headerName: "End Date",
            flex: 1,
            minWidth: 120,
        },
        {
            field: "approvedByName",
            headerName: "Processed By",
            flex: 1,
            minWidth: 120,
            renderCell: (params) => params.value || 'N/A',
        },
        {
            field: "displayProcessedAt",
            headerName: "Processed At",
            flex: 1,
            minWidth: 120,
        },
        {
            field: "actions",
            headerName: "Actions",
            flex: 0.5,
            minWidth: 80,
            sortable: false,
            renderCell: (params) => (
                <IconButton
                    onClick={(event) => handleMenuClick(event, params)}
                    size="small"
                >
                    <MoreVertIcon />
                </IconButton>
            ),
        },
    ];

    const WeeklyPatternEditor = ({ weeklyPattern, onChange, isEdit = false }) => (
        <Grid container spacing={2}>
            {weeklyPattern.map((day, index) => (
                <Grid item xs={12} key={day.dayOfWeek}>
                    <Card variant="outlined">
                        <CardContent sx={{ py: 1 }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                                <Typography variant="subtitle2" sx={{ minWidth: '80px' }}>
                                    {day.dayOfWeek}
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={day.isAvailable}
                                            onChange={(e) => onChange(index, 'isAvailable', e.target.checked)}
                                            size="small"
                                        />
                                    }
                                    label="Available"
                                />
                                {day.isAvailable && (
                                    <Box display="flex" gap={1} alignItems="center">
                                        <TextField
                                            type="time"
                                            label="Start"
                                            value={day.startTime || "09:00"}
                                            onChange={(e) => {
                                                e.preventDefault();
                                                onChange(index, 'startTime', e.target.value);
                                            }}
                                            size="small"
                                            sx={{ width: '120px' }}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{
                                                step: 300, // 5 min
                                            }}
                                        />
                                        <TextField
                                            type="time"
                                            label="End"
                                            value={day.endTime || "17:00"}
                                            onChange={(e) => {
                                                e.preventDefault();
                                                onChange(index, 'endTime', e.target.value);
                                            }}
                                            size="small"
                                            sx={{ width: '120px' }}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{
                                                step: 300, // 5 min
                                            }}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );

    const handleProcessOpen = (action) => {
        setProcessAction(action);
        setOpenProcessDialog(true);
        setRejectionReason('');
        handleMenuClose();
    };

    const handleProcessSubmit = async () => {
        if (processAction === 'Rejected' && !rejectionReason.trim()) {
            setValidationAlert({ show: true, message: "Rejection reason is required.", severity: 'error' });
            return;
        }

        try {
            await processAvailability({
                id: selectedRow._id,
                status: processAction,
                approvedBy: currentUserId,
                rejectionReason: processAction === 'Rejected' ? rejectionReason : undefined,
                userId: currentUserId,
            });
            setOpenProcessDialog(false);
            setRejectionReason('');
        } catch (error) {
    
            setValidationAlert({ show: true, message: `Failed to ${processAction.toLowerCase()} availability: ${error.data?.message || error.message}`, severity: 'error' });
        }
    };

    if (isLoadingAvailabilities || isLoadingEmployees) {
        return <Typography>Loading...</Typography>;
    }

    if (isErrorAvailabilities || isErrorEmployees) {
        return <Typography color="error">Error loading data.</Typography>;
    }

    return (
        <Box >
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                m="1.5rem 2.5rem"
            >
                <Typography variant="h4" color={theme.palette.secondary[100]}>
                    Employee Availability
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddDialogOpen}
                    sx={{
                        backgroundColor: theme.palette.secondary.light,
                        color: theme.palette.background.alt,
                        fontSize: "14px",
                        fontWeight: "bold",
                        padding: "10px 20px",
                    }}
                >
                    Add Availability
                </Button>
            </Box>

            {/* Filter Buttons */}
            <Box display="flex" gap="10px" m="1.5rem 2.5rem">
                {filterOptions.map((option) => (
                    <Button
                        key={option}
                        variant={selectedFilter === option ? "contained" : "outlined"}
                        onClick={() => setSelectedFilter(option)}
                        sx={{
                            backgroundColor: selectedFilter === option ? theme.palette.secondary.light : "transparent",
                            color: selectedFilter === option ? theme.palette.background.alt : theme.palette.secondary[100],
                            borderColor: theme.palette.secondary[100],
                        }}
                    >
                        {option}
                    </Button>
                ))}
            </Box>
            
            {/* Availability Data Grid */}
            <Box
                m="1.5rem 2.5rem"
                height="75vh"
                sx={{
                    "& .MuiDataGrid-root": { border: "none", backgroundColor: "transparent" },
                    "& .MuiDataGrid-row": {
                        borderBottom: `1px solid ${theme.palette.divider} !important`,
                        borderRadius: "1px", cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: theme.palette.secondary.light
                        }
                    },
                    "& .MuiDataGrid-cell": { borderBottom: "none" },
                    "& .name-column--cell": { color: theme.palette.secondary[200], fontWeight: "bold" },
                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: theme.palette.primary.light,
                        borderBottom: `1px solid ${theme.palette.secondary[300]} !important`,
                        color: theme.palette.secondary[100],
                    },
                    "& .MuiDataGrid-virtualScroller": { backgroundColor: theme.palette.background.alt },
                    "& .MuiDataGrid-footerContainer": {
                        borderTop: `1px solid ${theme.palette.secondary[300]} !important`,
                        backgroundColor: theme.palette.primary.light,
                        color: theme.palette.secondary[100],
                    },
                    "& .MuiDataGrid-toolbarContainer .MuiButton-text": { color: `${theme.palette.secondary[200]} !important`, fontSize: "16px" },
                }}
            >
                <DataGrid
                    loading={isLoadingAvailabilities || isLoadingEmployees}
                    getRowId={(row) => row._id}
                    rows={processedData}
                    columns={columns}
                    slots={{ toolbar: GridToolbar }}
                    rowHeight={isMobileScreen ? 80 : 100}
                />
            </Box>

            {/* Actions Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={handleViewOpen}>
                    <VisibilityIcon sx={{ mr: 1 }} />
                    View Details
                </MenuItem>
                {selectedRow?.status === 'Pending' && [
                    <MenuItem key="approve" onClick={() => handleProcessOpen('Approved')} sx={{ color: 'success.main' }}>
                        <EditIcon sx={{ mr: 1 }} />
                        Approve
                    </MenuItem>,
                    <MenuItem key="reject" onClick={() => handleProcessOpen('Rejected')} sx={{ color: 'error.main' }}>
                        <EditIcon sx={{ mr: 1 }} />
                        Reject
                    </MenuItem>
                ]}
                <MenuItem onClick={handleEditOpen}>
                    <EditIcon sx={{ mr: 1 }} />
                    Edit
                </MenuItem>
                <MenuItem onClick={handleDeleteOpen} sx={{ color: 'error.main' }}>
                    <DeleteIcon sx={{ mr: 1 }} />
                    Delete
                </MenuItem>
            </Menu>

            {/* View Dialog */}
            <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>Availability Details</DialogTitle>
                <DialogContent>
                    {selectedRow && (
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                {selectedRow.employeeName}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                Type: {selectedRow.typeOfAvailability}
                            </Typography>
                            {selectedRow.notes && (
                                <Typography variant="body2" gutterBottom>
                                    Notes: {selectedRow.notes}
                                </Typography>
                            )}
                            <Typography variant="body2" gutterBottom>
                                Period: {selectedRow.displayStartDate} - {selectedRow.displayEndDate}
                            </Typography>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                Weekly Schedule
                            </Typography>
                            <Grid container spacing={1}>
                                {selectedRow.weeklyPattern?.map((day) => (
                                    <Grid item xs={12} sm={6} md={4} key={day.dayOfWeek}>
                                        <Card variant="outlined" sx={{ p: 1 }}>
                                            <Typography variant="subtitle2">
                                                {day.dayOfWeek}
                                            </Typography>
                                            {day.isAvailable ? (
                                                <Typography variant="body2" color="success.main">
                                                    {day.startTime} - {day.endTime}
                                                </Typography>
                                            ) : (
                                                <Typography variant="body2" color="error.main">
                                                    Not Available
                                                </Typography>
                                            )}
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setOpenViewDialog(false)}
                            variant="contained"
                            color="secondary"
                            sx={{
                                backgroundColor: theme.palette.secondary.main,
                            }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Dialog */}
            <Dialog open={openAddDialog} onClose={handleAddDialogClose} maxWidth="md" fullWidth>
                <DialogTitle>Add New Availability</DialogTitle>
                <DialogContent>
                    {validationAlert.show && (
                        <Alert severity={validationAlert.severity} sx={{ mb: 2 }}>
                            {validationAlert.message}
                        </Alert>
                    )}
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Employee</InputLabel>
                                <Select
                                    name="employeeId"
                                    value={newAvailabilityData.employeeId}
                                    onChange={handleNewAvailabilityInputChange}
                                    label="Employee"
                                >
                                    {employeesData?.map((employee) => (
                                        <MenuItem key={employee._id} value={employee._id}>
                                            {employee.names.firstName} {employee.names.lastName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Type</InputLabel>
                                <Select
                                    name="typeOfAvailability"
                                    value={newAvailabilityData.typeOfAvailability}
                                    onChange={handleNewAvailabilityInputChange}
                                    label="Type"
                                >
                                    <MenuItem value="Permanent">Permanent</MenuItem>
                                    <MenuItem value="Temporary">Temporary</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        {newAvailabilityData.typeOfAvailability === 'Temporary' && (
                            <>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        name="startDate"
                                        label="Start Date"
                                        type="date"
                                        value={newAvailabilityData.startDate}
                                        onChange={handleNewAvailabilityInputChange}
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        name="endDate"
                                        label="End Date"
                                        type="date"
                                        value={newAvailabilityData.endDate}
                                        onChange={handleNewAvailabilityInputChange}
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        required
                                    />
                                </Grid>
                            </>
                        )}
                        {newAvailabilityData.typeOfAvailability === 'Permanent' && (
                            <Grid item xs={12}>
                                <Alert severity="info">
                                    Permanent availability does not require start and end dates. It will remain active until modified or deleted.
                                </Alert>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <TextField
                                name="notes"
                                label="Notes"
                                value={newAvailabilityData.notes}
                                onChange={handleNewAvailabilityInputChange}
                                fullWidth
                                multiline
                                rows={2}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                Weekly Schedule
                            </Typography>
                            <Box component="div" onSubmit={(e) => e.preventDefault()}>
                                <WeeklyPatternEditor
                                    weeklyPattern={newAvailabilityData.weeklyPattern}
                                    onChange={handleWeeklyPatternChange}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleAddDialogClose}
                            variant="contained"
                            color="secondary"
                            sx={{
                                backgroundColor: theme.palette.error.main,
                            }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAddSubmit}
                            variant="contained"
                            color="secondary"
                            sx={{
                                backgroundColor: theme.palette.secondary.main,
                            }}
                    >
                        Add Availability
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>Edit Availability</DialogTitle>
                <DialogContent>
                    {validationAlert.show && (
                        <Alert severity={validationAlert.severity} sx={{ mb: 2 }}>
                            {validationAlert.message}
                        </Alert>
                    )}
                    {editableAvailabilityData && (
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Employee</InputLabel>
                                    <Select
                                        name="employeeId"
                                        value={editableAvailabilityData.employeeId}
                                        onChange={handleInputChange}
                                        label="Employee"
                                    >
                                        {employeesData?.map((employee) => (
                                            <MenuItem key={employee._id} value={employee._id}>
                                                {employee.names.firstName} {employee.names.lastName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Type</InputLabel>
                                    <Select
                                        name="typeOfAvailability"
                                        value={editableAvailabilityData.typeOfAvailability}
                                        onChange={handleInputChange}
                                        label="Type"
                                    >
                                        <MenuItem value="Permanent">Permanent</MenuItem>
                                        <MenuItem value="Temporary">Temporary</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="startDate"
                                    label="Start Date"
                                    type="date"
                                    value={editableAvailabilityData.startDate}
                                    onChange={handleInputChange}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="endDate"
                                    label="End Date"
                                    type="date"
                                    value={editableAvailabilityData.endDate}
                                    onChange={handleInputChange}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    name="notes"
                                    label="Notes"
                                    value={editableAvailabilityData.notes || ''}
                                    onChange={handleInputChange}
                                    fullWidth
                                    multiline
                                    rows={2}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom>
                                    Weekly Schedule
                                </Typography>
                                <Box component="div" onSubmit={(e) => e.preventDefault()}>
                                    <WeeklyPatternEditor
                                        weeklyPattern={editableAvailabilityData.weeklyPattern}
                                        onChange={handleEditWeeklyPatternChange}
                                        isEdit={true}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                    onClick={() => setOpenEditDialog(false)}
                        variant="contained"
                        color="secondary"
                        sx={{
                            backgroundColor: theme.palette.error.main,
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleEditSubmit}
                        variant="contained"
                        color="secondary"
                            sx={{
                                backgroundColor: theme.palette.secondary.main,
                            }}
                    >
                        Update Availability
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
                <DialogTitle>Delete Availability</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the availability record for {selectedRow?.employeeName}?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleDelete}
                        variant="contained"
                        color="secondary"
                        sx={{
                            backgroundColor: theme.palette.error.main,
                        }}
                    >
                        Delete
                    </Button>
                    <Button
                        onClick={() => setOpenDeleteDialog(false)}
                        variant="contained"
                        color="secondary"
                        sx={{
                            backgroundColor: theme.palette.secondary.main,
                        }}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Process Dialog */}
            <Dialog open={openProcessDialog} onClose={() => setOpenProcessDialog(false)}>
                <DialogTitle>Process Availability</DialogTitle>
                <DialogContent>
                    {validationAlert.show && (
                        <Alert severity={validationAlert.severity} sx={{ mb: 2 }}>
                            {validationAlert.message}
                        </Alert>
                    )}
                    <FormControl fullWidth>
                        <InputLabel>Action</InputLabel>
                        <Select
                            value={processAction}
                            onChange={(e) => setProcessAction(e.target.value)}
                            label="Action"
                        >
                            <MenuItem value="Approved">Approve</MenuItem>
                            <MenuItem value="Rejected">Reject</MenuItem>
                        </Select>
                    </FormControl>
                    {processAction === 'Rejected' && (
                        <TextField
                            label="Rejection Reason"
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            fullWidth
                            multiline
                            rows={3}
                            sx={{ mt: 2 }}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenProcessDialog(false)}>Cancel</Button>
                    <Button onClick={handleProcessSubmit} variant="contained">
                        Process
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Availability;
